import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Cookie-based auth (HttpOnly). No token is ever stored in JS/localStorage.
const api = axios.create({ baseURL: API, withCredentials: true });

export const OFFLINE_MSG = "Connessione assente. Riprova quando K9 Manager è online.";

// --- Shared connectivity / API-reachability state ---
let apiOnline = true;
const connListeners = new Set();
export const getApiOnline = () => apiOnline;
export const onConnectivityChange = (fn) => { connListeners.add(fn); return () => connListeners.delete(fn); };
const setApiOnline = (v) => {
  if (apiOnline === v) return;
  apiOnline = v;
  connListeners.forEach((f) => { try { f(v); } catch (e) { /* noop */ } });
};

// --- In-memory (non-persistent) last-successful GET cache ---
const getCache = new Map();
const cacheKey = (url, config) => `${url}::${JSON.stringify(config?.params || {})}`;

const isAuthFlow = (url = "") =>
  url.includes("/auth/refresh") || url.includes("/auth/login") ||
  url.includes("/auth/logout") || url.includes("/auth/invite") ||
  url.includes("/auth/forgot-password") || url.includes("/auth/reset-password");

api.interceptors.request.use((config) => {
  const method = (config.method || "get").toLowerCase();
  const isWrite = !["get", "head", "options"].includes(method);
  // Fail-closed write guard: never send business mutations while offline.
  if (isWrite && (navigator.onLine === false || apiOnline === false)) {
    const err = new Error(OFFLINE_MSG);
    err.isOffline = true;
    return Promise.reject(err);
  }
  return config;
});

// Single-flight refresh: only one /auth/refresh in progress at a time.
let refreshing = null;

api.interceptors.response.use(
  (res) => { setApiOnline(true); return res; },
  async (err) => {
    if (err.isOffline) return Promise.reject(err);
    if (!err.response) {
      // No HTTP response => network/API unreachable (authoritative).
      setApiOnline(false);
      return Promise.reject(err);
    }
    setApiOnline(true);
    const original = err.config || {};
    const status = err.response.status;

    // Access token likely expired: try one silent refresh, then retry.
    if (status === 401 && !original.__isRetry && !isAuthFlow(original.url || "")) {
      try {
        refreshing = refreshing || api.post("/auth/refresh");
        await refreshing;
        refreshing = null;
        original.__isRetry = true;
        return api(original);
      } catch (e) {
        refreshing = null;
        // Refresh failed => no valid session. Let /auth/me callers handle it
        // (AuthContext), otherwise send the user to login.
        const isMe = (original.url || "").includes("/auth/me");
        if (!isMe && window.location.pathname !== "/login") {
          window.location.href = "/login";
        }
        return Promise.reject(err);
      }
    }
    return Promise.reject(err);
  }
);

// GET with last-successful in-memory fallback.
// Resolves { data, stale }. Throws only on a network failure with NO cached value (cold start).
export const apiGet = async (url, config = {}) => {
  const key = cacheKey(url, config);
  try {
    const res = await api.get(url, config);
    getCache.set(key, res.data);
    return { data: res.data, stale: false };
  } catch (e) {
    if (!e.response && getCache.has(key)) {
      return { data: getCache.get(key), stale: true };
    }
    throw e;
  }
};

export const apiErr = (e, fallback = "Si è verificato un errore.") => {
  const d = e?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x?.msg || "").filter(Boolean).join(" ") || fallback;
  return e?.message || fallback;
};

export default api;
